/**
 * TimelineItem Component
 * 
 * Memoized timeline item to prevent unnecessary re-renders.
 * Only re-renders when its specific props change.
 */
import React, { memo } from 'react';
import { View, Flex, IconButton } from '@smarterservices/smarter-ui';

const TimelineItem = memo(({
    item,
    index,
    totalItems,
    isHighlighted,
    baseId,
}) => {
    /**
     * Determines the margin for the vertical line based on item position
     */
    const figureMargin = () => {
        if (index === 0) {
            return '15px 0 0 0';
        } else if (index === totalItems - 1) {
            return '0 0 5px 0';
        }
    };

    /**
     * Calculates the height for the vertical line
     */
    const figureHeight = () => {
        const hasDetail = item?.detail && item.detail.length;
        if (index !== totalItems - 1) {
            return '105%';
        } else if (index === totalItems - 1 && hasDetail) {
            return '105%';
        } else {
            return '10px';
        }
    };

    return (
        <View
            as="div"
            key={`${baseId}-item-${index}`}
            id={item.id}
            background={isHighlighted ? "secondary" : "transparent"}
            borderRadius="medium"
            borderWidth="small"
            borderColor={isHighlighted ? "primary" : "transparent"}
            data-time={item.time !== undefined ? item.time : null}
            style={{
                transition: 'all 0.3s ease-in-out'
            }}
        >
            <Flex as="div" className="timeline-item" alignItems="stretch" style={{ height: '100%' }} padding="none none none small">
                <Flex.Item as="div" className="timeline-item-label" align="start" style={{ height: '100%' }}>
                    <View as="div" margin="none none small none">
                        <View as="div" margin="xx-small xx-small none none">
                            {item.label}
                        </View>
                    </View>
                </Flex.Item>
                <Flex.Item as="div" position="relative" style={{ height: '100%' }}>
                    {/* Vertical line */}
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: '50%',
                            width: '1px',
                            height: figureHeight(),
                            backgroundColor: '#ccc',
                            margin: figureMargin(),
                        }}
                    />
                    <View as="div"
                        margin="none none small none"
                        padding="xxx-small none none none">
                        {(item.icon && item.icon?.icon?.type?.displayName?.toLowerCase().includes('icon')) ? (
                            <>
                                <IconButton
                                    as={
                                        item.icon?.onClick || item.icon?.onContextMenu
                                            ? 'button'
                                            : 'span'
                                    }
                                    cursor={
                                        item.icon?.onClick || item.icon?.onContextMenu
                                            ? 'pointer'
                                            : 'default'
                                    }
                                    interaction={
                                        item.icon?.onClick || item.icon?.onContextMenu
                                            ? 'enabled'
                                            : 'readonly'
                                    }
                                    shape={item.icon?.shape ? item.icon?.shape : 'circle'}
                                    screenReaderLabel={
                                        item.icon?.screenReaderLabel
                                            ? item.icon?.screenReaderLabel
                                            : 'Timeline Item'
                                    }
                                    margin="xxx-small"
                                    withBorder={item.icon?.withBorder || false}
                                    color={item.icon?.color || 'primary'}
                                    size={item.icon?.size || 'small'}
                                    onClick={item.icon?.onClick}
                                    onContextMenu={item.icon?.onContextMenu}
                                    className="timeline-icon-button"
                                >
                                    {item.icon.icon}
                                </IconButton>
                            </>
                        ) : (
                            <>
                                {item?.icon && item.icon}
                            </>
                        )}
                    </View>
                </Flex.Item>
                <Flex.Item
                    as="div"
                    shouldGrow
                    shouldShrink
                    align="start"
                    style={{ height: '100%' }}
                >
                    <View as="div"
                        className="timeline-item-content"
                        margin="none none small none">
                        <View as="div" margin="xx-small none none x-small">
                            {item.title}
                        </View>
                        {item.detail && (
                            <View as="div" margin="none none none x-small">
                                {item.detail}
                            </View>
                        )}
                    </View>
                </Flex.Item>
            </Flex>
        </View>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function - only re-render if these specific props change
    return (
        prevProps.item.id === nextProps.item.id &&
        prevProps.isHighlighted === nextProps.isHighlighted &&
        prevProps.index === nextProps.index &&
        prevProps.totalItems === nextProps.totalItems &&
        // Deep check for item changes that matter
        prevProps.item.time === nextProps.item.time &&
        prevProps.item.label === nextProps.item.label &&
        prevProps.item.title === nextProps.item.title
    );
});

TimelineItem.displayName = 'TimelineItem';

export default TimelineItem;
